import { FunctionComponent, useCallback, useEffect } from 'react'
import { useScreenSize } from '@/modules/desktopMobile/screenSizeContext'
import Loading from '@/components/Loading/Loading'
import { useTranslation } from 'react-i18next'
import Planning from './planning'
import { useAuth } from '@/modules/context/AuthContext'
import { Location, Navigate, useLocation } from 'react-router-dom'
import { usePlanningData } from './hooks/usePlanningData.ts'
import { PlanningRights, PlanningTabsState, usePlanningRights, usePlanningTabs } from '@/hooks/usePlanningRights'
import { usePlanningSlots } from './hooks/usePlanningSlots'
import { useSmoothLoading } from '@/hooks/useSmoothLoading'
import AnnuaireTrackingHandler from '@/components/AnnuaireTracking/AnnuaireTrackingHandler'
import { Role } from '@/types/Role.ts'
import { useRefetchOnFocusOrOnline } from '@/hooks/useRefetchOnFocusOrOnline.ts'

const PlanningHandler: FunctionComponent = () => {
    const { t } = useTranslation()
    const { user } = useAuth()
    const isMobile: boolean = useScreenSize()

    const urlDaycare: Location = useLocation()
    const urlSearchParams: URLSearchParams = new URLSearchParams(urlDaycare.search)
    const tabParam: string | null = urlSearchParams.get('tab')

    const rights: PlanningRights = usePlanningRights()
    const planningTabs: PlanningTabsState = usePlanningTabs(t, tabParam)

    const { parent, childs, places, staffs, refetchAll } = usePlanningData(user)

    useEffect(() => {
        if (!user?.currentDaycare?.id) return
        refetchAll()
    }, [user?.currentDaycare?.id, refetchAll])

    const {
        slots,
        rateSlots,
        calendarEvents,
        requestNumber,
        lastFromDate,
        lastToDate,
        isLoading: slotsLoading,
        fetchData,
        refetchData
    } = usePlanningSlots({
        rights,
        isStaff: planningTabs.isStaffTab,
        currentDaycare: user?.currentDaycare,
        role: user?.role
    })

    const refetchDataCallback: () => void = useCallback(
        () => refetchData(planningTabs.isGlobalTab),
        [refetchData, planningTabs.isGlobalTab]
    )

    useRefetchOnFocusOrOnline(refetchDataCallback)

    const isLoading: boolean = useSmoothLoading(slotsLoading)

    if (!user) {
        return <Navigate to='/' replace />
    } else if (!user.currentDaycare || !user.currentDaycareRights) {
        return <Navigate to='/daycare' replace />
    }

    return (
        <>
            <AnnuaireTrackingHandler role={user.role || Role.parent} />
            {isLoading && <Loading overall />}
            <Planning
                planningTabs={planningTabs}
                planningRights={rights}
                fetchData={fetchData}
                refetchData={refetchData}
                currentDaycare={user.currentDaycare}
                calendarEvents={calendarEvents}
                closingDays={slots.getClosingDay}
                works={slots.getWorks}
                places={places}
                childs={childs}
                staffsListData={staffs}
                parent={parent}
                lastFromDate={lastFromDate}
                lastToDate={lastToDate}
                requestNumber={requestNumber}
                isMobile={isMobile}
                rateSlots={rateSlots}
            />
        </>
    )
}

export default PlanningHandler
